import type { ReactNode } from "react";
import type { Placement } from "react-aria";
import type {
  MenuItemProps as ReactAriaMenuItemProps,
  MenuProps as ReactAriaMenuProps,
  Selection,
} from "react-aria-components";
import {
  Menu as ReactAriaMenu,
  MenuItem as ReactAriaMenuItem,
  Separator as ReactAriaMenuSeparator,
  MenuTrigger as ReactAriaMenuTrigger,
  Popover as ReactAriaPopover,
} from "react-aria-components";

import { cn, type VariantProps } from "../utils";
import { NcIconTick } from "./nc-icons";
import { ncListBoxClasses } from "./nc-list-box";
import { ncPopoverClasses } from "./nc-popover";

interface NcMenuProps<T extends object> extends ReactAriaMenuProps<T> {
  placement?: Placement;
}

const NcMenu = function <T extends object>({ placement, className, ...props }: NcMenuProps<T>) {
  return (
    <ReactAriaPopover
      data-nc="NcMenu"
      className={cn(ncPopoverClasses.popover, "min-w-(--trigger-width) cursor-pointer", className)}
      placement={placement}
    >
      <ReactAriaMenu
        className={cn(ncListBoxClasses.list({ variant: "default" }), "max-h-120 overflow-y-auto")}
        {...props}
      />
    </ReactAriaPopover>
  );
};

export interface NcMenuItemProps
  extends ReactAriaMenuItemProps,
    VariantProps<typeof ncListBoxClasses.item> {
  children: ReactNode;
}

const MenuItem = ({ children, className, variant = "default", ...props }: NcMenuItemProps) => (
  <ReactAriaMenuItem
    data-nc="NcMenu.Item"
    className={cn(
      ncListBoxClasses.item({ variant }),
      "cursor-pointer not-last:border-b-0 first-of-type:rounded-t-[0.15rem] last-of-type:rounded-b-[0.15rem]",
      className
    )}
    {...props}
  >
    {children}
    <div aria-hidden="true" className={ncListBoxClasses.icon}>
      <NcIconTick />
    </div>
  </ReactAriaMenuItem>
);

const Separator = ({ ...props }) => <ReactAriaMenuSeparator className="bg-ui h-px" {...props} />;

NcMenu.Item = MenuItem;
NcMenu.Trigger = ReactAriaMenuTrigger;
NcMenu.Separator = Separator;

export type NcMenuSelection = Selection;

export { NcMenu };
